import type { Phase, PollSettings, PollStatus } from '@val/shared'
import { useCallback, useEffect, useState } from 'react'
import { fetchSettings, refreshNow, updateSettings } from '../lib/api.ts'
import { clockTime } from '../lib/format.ts'

const INTERVALS: { ms: number; label: string }[] = [
  { ms: 60_000, label: 'Varje minut' },
  { ms: 120_000, label: 'Varannan minut' },
  { ms: 300_000, label: 'Var 5:e minut' },
  { ms: 900_000, label: 'Var 15:e minut' },
  { ms: 1_800_000, label: 'Var 30:e minut' },
  { ms: 3_600_000, label: 'En gång i timmen' },
]

const PHASE_LABELS: Record<Phase, string> = {
  preliminar: 'Preliminärt (valnatten)',
  slutlig: 'Slutligt (rösträkning veckan efter)',
  genrep: 'Generalrepetition (test)',
}

export function PollSettingsCard() {
  const [status, setStatus] = useState<PollStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setStatus(await fetchSettings())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunde inte hämta inställningar')
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const save = async (patch: Partial<PollSettings>) => {
    setBusy(true)
    setError(null)
    try {
      setStatus(await updateSettings(patch))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunde inte spara')
      await load()
    } finally {
      setBusy(false)
    }
  }

  const doRefresh = async () => {
    setBusy(true)
    setError(null)
    setRefreshMsg(null)
    try {
      const r = await refreshNow()
      setRefreshMsg(r.changed ? `Uppdaterat (${r.reason})` : `Oförändrat (${r.reason})`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Hämtning misslyckades')
    } finally {
      setBusy(false)
    }
  }

  if (!status) {
    return (
      <div className="card card__pad">
        <div className="card__title">Uppdatering från Valmyndigheten</div>
        {error ? (
          <div className="notice notice--error">{error}</div>
        ) : (
          <p className="dim">Laddar …</p>
        )}
      </div>
    )
  }

  const knownInterval = INTERVALS.some((i) => i.ms === status.pollIntervalMs)

  return (
    <div className="card card__pad pollsettings">
      <div className="card__title">Uppdatering från Valmyndigheten</div>
      {error && <div className="notice notice--error">{error}</div>}

      <label className="pollsettings__toggle">
        <input
          type="checkbox"
          checked={status.pollEnabled}
          disabled={busy}
          onChange={(e) => save({ pollEnabled: e.target.checked })}
        />
        <span>
          <strong>Automatisk hämtning {status.pollEnabled ? 'på' : 'av'}</strong>
          <span className="dim">
            {' '}
            –{' '}
            {status.pollEnabled ? 'appen hämtar nytt resultat automatiskt' : 'inget hämtas just nu'}
          </span>
        </span>
      </label>

      <div className="pollsettings__row">
        <label className="field">
          <span>Hur ofta</span>
          <select
            value={status.pollIntervalMs}
            disabled={busy || !status.pollEnabled}
            onChange={(e) => save({ pollIntervalMs: Number(e.target.value) })}
          >
            {!knownInterval && (
              <option value={status.pollIntervalMs}>
                {Math.round(status.pollIntervalMs / 1000)} sekunder
              </option>
            )}
            {INTERVALS.map((i) => (
              <option key={i.ms} value={i.ms}>
                {i.label}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Datamängd</span>
          <select
            value={status.activePhase}
            disabled={busy}
            onChange={(e) => save({ activePhase: e.target.value as Phase })}
          >
            {(Object.keys(PHASE_LABELS) as Phase[]).map((p) => (
              <option key={p} value={p}>
                {PHASE_LABELS[p]}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="pollsettings__status">
        <span className="dim">
          {status.lastFetchedAt
            ? `Senast hämtad ${clockTime(status.lastFetchedAt)}`
            : 'Inget hämtat ännu'}
          {status.lastOutcome ? ` · ${status.lastOutcome}` : ''}
        </span>
        <button type="button" className="btn" onClick={doRefresh} disabled={busy}>
          Hämta nu
        </button>
      </div>
      {refreshMsg && <div className="notice">{refreshMsg}</div>}
    </div>
  )
}
